import { createAction } from 'redux-starter-kit';
import {
  requestCancelled, requestFailed, requestStart, requestSucceded,
} from '../../../domain/requests/RequestActions';
import { get, post, put } from '../../../../utility/via-fetch';
import { addMessage } from '../../application/messageUiActions';
import { putChecklist, putChecklists } from '../../../domain/checklists/checklistsActions';
import { putChecklistCategoryConfig, putChecklistConfig, putChecklistsConfig } from '../../../domain/checklistsConfig/checklistsConfigActions';
import { putChecklistCategories, putChecklistCategory } from '../../../domain/checklistCategories/checklistCategoriesActions';
import { putChecklistItem, putChecklistItems } from '../../../domain/checklistItems/checklistItemsActions';


/**
 * An action that signifies a request to load checklist data has been made.
 */
export const fetchChecklistsPageData = createAction('FETCH_CHECKLISTS_PAGE_DATA');

/**
 * An action that signifies a request to load checklist data has been made.
 */
export const fetchChecklistPageData = createAction('FETCH_CHECKLIST_PAGE_DATA');

/**
 * An action for loading all the data for the checklists page.
 */
export function requestChecklistsPageData() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchChecklistsPageData.toString()));
    dispatch(fetchChecklistsPageData(requestId));

    try {
      const [checklists, checklistsConfig] = await Promise.all([get('/api/checklists/'), get('/api/configuration/checklists/')]);

      // Normalise data. Store the data in the domain, and the ids in the request response.
      dispatch(putChecklists(checklists));
      dispatch(putChecklistsConfig(checklistsConfig));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action for loading all the data for the checklist edit page.
 */
export function requestChecklistPageData(checklistId) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchChecklistPageData.toString()));

    try {
      const [checklists, categories, items, checklistsConfig] = await Promise.all([
        get('/api/checklists/'),
        get('/api/checklist-categories/'),
        get('/api/checklist-items/'),
        get('/api/configuration/checklists/'),
      ]);

      // Action request and update edit state with current checklist and its data
      const checklistData = checklistsConfig.find(configRow => configRow.checkListCode === checklistId) || null;
      dispatch(fetchChecklistPageData({ requestId, checklistId, checklistData }));

      // Normalise data. Store the data in the domain, and the ids in the request response.
      dispatch(putChecklists(checklists));
      dispatch(putChecklistCategories(categories));
      dispatch(putChecklistItems(items));
      dispatch(putChecklistsConfig(checklistsConfig));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * Publish (active) actions.
 */
export const addPublishChecklist = createAction('ADD_PENDING_PUBLISH_CHECKLIST');
export const removePublishChecklist = createAction('REMOVE_PENDING_PUBLISH_CHECKLIST');

export function requestPublishChecklist(checklist) {
  const toggledChecklist = { ...checklist, active: !checklist.active };

  return async (dispatch) => {
    const requestId = dispatch(requestStart(addPublishChecklist.toString()));
    dispatch(addPublishChecklist(toggledChecklist.checkListCode));
    try {
      await post(`/api/checklists/${checklist.checkListCode}`, {}, toggledChecklist);
      dispatch(putChecklist(toggledChecklist));
      dispatch(addMessage('success', 'itemStateUpdated', { item: checklist.description }));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
    dispatch(removePublishChecklist(toggledChecklist.checkListCode));
  };
}

/**
 * Update checklist title.
 */
export const updateChecklistTitle = createAction('UPDATE_CHECKLIST_TITLE');

export function requestUpdateChecklistTitle(checklist, setPopupVisible) {
  const amendChecklistConfig = { ...checklist };
  const amendChecklist = { ...amendChecklistConfig };
  delete amendChecklist.checkListCategories;

  return async (dispatch) => {
    const requestId = dispatch(requestStart(updateChecklistTitle.toString()));
    try {
      await post(`/api/checklists/${checklist.checkListCode}`, {}, amendChecklist);
      dispatch(putChecklist(amendChecklist));
      dispatch(putChecklistConfig(amendChecklistConfig));
      dispatch(updateChecklistTitle(amendChecklistConfig));
      dispatch(addMessage('success', 'questionUpdated', { item: amendChecklist.description }));
      dispatch(requestSucceded(requestId, amendChecklistConfig));
      setPopupVisible(false);
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * Cancel the update of checklist title.
 */
export const cancelChecklistTitle = createAction('CANCEL_CHECKLIST_TITLE');

export function cancelUpdateChecklistTitleRequest(prevChecklistDesc) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(updateChecklistTitle.toString()));
    dispatch(requestCancelled(requestId));
    dispatch(cancelChecklistTitle(prevChecklistDesc));
  };
}

/**
 * Actions for various updates below.
 */
export const updateChecklistConfig = createAction('UPDATE_CHECKLIST_CONFIG');
export const updateChecklistCategoryConfig = createAction('UPDATE_CHECKLIST_CATEGORY_CONFIG');

/**
 * Create new checklist.
 */
export const addChecklist = createAction('ADD_CHECKLIST');

export function requestAddChecklist(checklist, successFunction) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(addChecklist.toString()));
    try {
      const newId = await post('/api/checklists', {}, checklist);
      // eslint-disable-next-line no-param-reassign
      checklist.checkListCode = newId.toString();
      dispatch(putChecklist(checklist));
      dispatch(putChecklistConfig(checklist));
      dispatch(addMessage('success', 'itemSaved', { item: checklist.description }));
      dispatch(requestSucceded(requestId));
      successFunction(newId);
      dispatch(addChecklist(checklist.description));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * Begin the checklist question create.
 */
export const beginAddChecklist = createAction('BEGIN_ADD_CHECKLIST');

export function beginChecklistAddRequest() {
  return async (dispatch) => {
    dispatch(beginAddChecklist());
  };
}

/**
 * Cancel the checklist question create.
 */
export const cancelChecklistAdd = createAction('CANCEL_ADD_CHECKLIST');

export function cancelChecklistAddRequest() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(addChecklist.toString()));
    dispatch(requestCancelled(requestId));
    dispatch(cancelChecklistAdd());
  };
}

/**
 * Create new category and link to checklist.
 */
export const addChecklistCategory = createAction('ADD_CATEGORY_TO_CHECKLIST');
const createCategory = createAction('CREATE_CHECKLIST_CATEGORY');

export function requestAddChecklistCategory(checklist, categoryIn, setPopupClose) {
  const category = { ...categoryIn };
  const checklistConfig = { ...JSON.parse(JSON.stringify(checklist)) };

  return async (dispatch) => {
    const requestId = dispatch(requestStart(addChecklistCategory.toString()));
    dispatch(addChecklistCategory(requestId));
    try {
      // Create category if needed
      if (!category.categoryCode) {
        dispatch(createCategory);
        const newId = await post('/api/checklist-categories/', {}, category);
        // eslint-disable-next-line no-param-reassign
        category.categoryCode = newId.toString();
        dispatch(putChecklistCategory(category));
        dispatch(addMessage('success', 'itemSaved', { item: category.categoryDescription }));
      }

      // Update checklist - save
      const categories = [];
      checklist.checkListCategories.forEach((cat, index) => {
        // eslint-disable-next-line no-param-reassign
        delete cat.checkListItems;
        // eslint-disable-next-line no-param-reassign
        cat.rankInCheckList = index;
        categories.push(cat);
      });
      const newCategory = { ...category, rankInCheckList: categories.length };
      categories.push(newCategory);
      await put(`/api/checklists/${checklist.checkListCode}/categories`, {}, categories);

      // Update checklist - config
      // eslint-disable-next-line no-param-reassign
      newCategory.checkListItems = [];
      let maxRank = 0;
      if (checklistConfig.checkListCategories && checklistConfig.checkListCategories.length > 0) {
        maxRank = Math.max(...checklistConfig.checkListCategories.map(cat => cat.rankInCheckList));
      }
      newCategory.rankInCheckList = maxRank ? maxRank + 1 : 1;
      checklistConfig.checkListCategories.push(newCategory);
      dispatch(putChecklistConfig(checklistConfig));
      dispatch(updateChecklistConfig(checklistConfig));
      dispatch(addMessage('success', 'questionUpdated', { item: checklist.description }));
      dispatch(requestSucceded(requestId));
      setPopupClose();
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * Cancel the checklist category create.
 */
export const cancelChecklistAddCategory = createAction('CANCEL_CHECKLIST_ADD_CATEGORY');

export function cancelChecklistAddCategoryRequest() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(addChecklistCategory.toString()));
    dispatch(requestCancelled(requestId));
    dispatch(cancelChecklistAddCategory());
  };
}

/**
 * Create new question and link to category on checklist.
 */
export const addChecklistQuestion = createAction('ADD_ITEM_TO_CHECKLIST');
const createQuestion = createAction('CREATE_CHECKLIST_ITEM');

export function requestAddChecklistQuestion(checklist, categoryId, questionIn, setPopupClose) {
  const question = { ...questionIn };
  const checklistConfig = { ...JSON.parse(JSON.stringify(checklist)) };

  return async (dispatch) => {
    const requestId = dispatch(requestStart(addChecklistQuestion.toString()));
    dispatch(addChecklistQuestion(requestId));
    try {
      // Create question if needed
      if (!question.itemCode) {
        dispatch(createQuestion);
        const newId = await post('/api/checklist-items/', {}, question);
        // eslint-disable-next-line no-param-reassign
        question.itemCode = newId.toString();
        dispatch(putChecklistItem(question));
        dispatch(addMessage('success', 'itemSaved', { item: question.itemDescription }));
      }

      // Update checklist category - save
      const questions = [];
      const category = checklist.checkListCategories.find(cat => cat.categoryCode === categoryId);
      category.checkListItems.forEach((itm, index) => {
        // eslint-disable-next-line no-param-reassign
        itm.rankInCategory = index + 1;
        questions.push(itm);
      });
      const newQuestion = { ...question, rankInCategory: questions.length + 1 };
      questions.push(newQuestion);

      await put(`/api/checklists/${checklist.checkListCode}/categories/${categoryId}/items`, {}, questions);

      // Update checklist - config
      const categoryConfig = checklistConfig.checkListCategories.find(cat => cat.categoryCode === categoryId);
      let maxRank = 0;
      if (categoryConfig.checkListItems && categoryConfig.checkListItems.length > 0) {
        maxRank = Math.max(...categoryConfig.checkListItems.map(itm => itm.rankInCategory));
      }
      newQuestion.rankInCategory = maxRank ? maxRank + 1 : 1;
      categoryConfig.checkListItems.push(newQuestion);
      dispatch(putChecklistConfig(checklistConfig));
      dispatch(updateChecklistConfig(checklistConfig));
      dispatch(addMessage('success', 'questionUpdated', { item: checklist.description }));
      dispatch(requestSucceded(requestId));
      setPopupClose();
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * Cancel the checklist question create.
 */
export const cancelChecklistAddQuestion = createAction('CANCEL_CHECKLIST_ADD_QUESTION');

export function cancelChecklistAddQuestionRequest() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(addChecklistQuestion.toString()));
    dispatch(requestCancelled(requestId));
    dispatch(cancelChecklistAddQuestion());
  };
}

/**
 * Remove checklist category (and all questions inside it).
 */
export const removeChecklistCategory = createAction('REMOVE_CHECKLIST_CATEGORY');

export function requestChecklistCategoryRemoval(catKey, checklist) {
  const categoryId = catKey.split('-')[1];

  // Config with category removed
  const newCatConfig = { ...checklist }.checkListCategories.filter(category => category.categoryCode !== categoryId);
  const newConfig = { ...checklist, checkListCategories: newCatConfig };

  // Create category list for save
  const newCategories = [];
  newCatConfig.forEach((catConfig) => {
    const newCat = { ...catConfig };
    delete newCat.checkListItems;
    newCategories.push(newCat);
  });

  return async (dispatch) => {
    const requestId = dispatch(requestStart(removeChecklistCategory.toString()));
    try {
      await put(`/api/checklists/${checklist.checkListCode}/categories`, {}, newCategories);
      dispatch(putChecklistConfig(newConfig));
      dispatch(updateChecklistConfig(newConfig));
      dispatch(addMessage('success', 'questionUpdated', { item: newConfig.description }));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * Remove checklist questions from a category.
 */
export const removeChecklistQuestion = createAction('REMOVE_CHECKLIST_CATEGORY_ITEMS');

export function requestChecklistQuestionRemoval(itmKey, checklist) {
  const categoryId = itmKey.split('-')[1];
  const itemId = itmKey.split('-')[2];

  // Config with question removed (extra working to prevent state change that fails)
  const newItems = []; // for save
  const newCategories = [];
  const theConfig = { ...checklist };
  theConfig.checkListCategories.forEach((catConfig) => {
    if (catConfig.categoryCode === categoryId) {
      const catNewConfig = catConfig.checkListItems.filter(item => item.itemCode !== itemId);
      // Items left
      catNewConfig.forEach((itemConfig) => {
        newItems.push(itemConfig);
      });
      newCategories.push({ ...catConfig, checkListItems: newItems });
    } else {
      newCategories.push(catConfig);
    }
  });
  const newConfig = { ...checklist, checkListCategories: newCategories };

  return async (dispatch) => {
    const requestId = dispatch(requestStart(removeChecklistQuestion.toString()));
    try {
      await put(`/api/checklists/${checklist.checkListCode}/categories/${categoryId}/items`, {}, newItems);
      dispatch(putChecklistConfig(newConfig));
      dispatch(updateChecklistConfig(newConfig));
      dispatch(addMessage('success', 'questionUpdated', { item: checklist.description }));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * Reorder Checklist Items
 * Either - reorder categories (category key -1)
 *     or - reorder items within a category (if more than one category affected, this method will be called for each)
 */
export const reorderChecklist = createAction('REORDER_CHECKLIST_CATEGORIES_OR_ITEMS');

export function requestChecklistReorder(order, checklist, categoryKey) {
  const checklistConfig = { ...checklist };
  const oldCategories = [];
  const oldConfigCategories = [];
  const oldItems = [];
  const newCategories = [];
  const newItems = [];
  let otherCatKey = -1;
  let itemsReduced = false;
  const otherCategoryItems = [];

  // New Config
  const newConfig = { ...JSON.parse(JSON.stringify(checklistConfig)) };
  if (categoryKey === -1) {
    newConfig.checkListCategories = [];
  }

  // Process order if valid
  if (order && order.length > 0) {
    // Old Values
    checklistConfig.checkListCategories.forEach((cat) => {
      // Keep Items
      cat.checkListItems.forEach((itm) => {
        const oldItm = { ...itm };
        delete oldItm.rankInCategory;
        oldItems.push(oldItm);
      });
      // Keep category config
      oldConfigCategories.push({ ...cat });
      // Keep category without its items
      const oldCat = { ...cat };
      delete oldCat.checkListItems;
      delete oldCat.rankInCheckList;
      oldCategories.push(oldCat);
    });

    // Items reduced (item moved out)
    if (categoryKey > -1) {
      const oldCat = oldConfigCategories.find(oCat => oCat.categoryCode === categoryKey);
      itemsReduced = oldCat.checkListItems.length > order.length;
    }

    // In case of category to category move do not process the reduced category
    // Processed as other category below when processing the enlarged category
    if (itemsReduced) {
      return () => {};
    }

    // New order
    order.forEach((code) => {
      if (code !== 'ignore') {
        const categoryId = code.split('-')[1];

        if (categoryKey === -1) {
          // Category reorder - save & config
          newCategories.push(oldCategories.find(fCat => fCat.categoryCode === categoryId));
          newConfig.checkListCategories.push(oldConfigCategories.find(cCat => cCat.categoryCode === categoryId));
        } else {
          const itemId = code.split('-')[2];

          // Other category
          if (categoryId !== categoryKey) {
            otherCatKey = categoryId;
            const otherCat = oldConfigCategories.find(cCat => cCat.categoryCode === otherCatKey);
            otherCat.checkListItems.forEach((oItem) => {
              if (oItem.itemCode !== itemId) {
                otherCategoryItems.push(oItem);
              }
            });
          }

          // Question reorder - save & config
          newItems.push(oldItems.find(fItm => fItm.itemCode === itemId));
        }
      }
    });

    // Ranking
    newCategories.forEach((cat, index) => {
      // eslint-disable-next-line no-param-reassign
      cat.rankInCheckList = index + 1;
    });
    newItems.forEach((item, index) => {
      // eslint-disable-next-line no-param-reassign
      item.rankInCategory = index + 1;
    });

    const newCategoryConfig = {
      checklistId: checklist.checkListCode,
      categoryId: categoryKey,
      category: [],
    };
    const otherCategoryConfig = {
      checklistId: checklist.checkListCode,
      categoryId: otherCatKey,
      category: [],
    };

    // Finish off new config for new items
    if (categoryKey !== -1) {
      // New Items
      const theCat = newConfig.checkListCategories.find(cCat => cCat.categoryCode === categoryKey);
      theCat.checkListItems = [];
      newItems.map(item => theCat.checkListItems.push(item));
      newCategoryConfig.category = theCat;

      // Other Cat Items
      if (otherCatKey > -1) {
        const otherCat = newConfig.checkListCategories.find(cCat => cCat.categoryCode === otherCatKey);
        otherCat.checkListItems = [];
        otherCategoryItems.map(item => otherCat.checkListItems.push(item));
        otherCategoryConfig.category = otherCat;
      }
    }

    return async (dispatch) => {
      const requestId = dispatch(requestStart(reorderChecklist.toString()));
      try {
        // Change state immediately to keep screen from re-painting or moving too much
        if (categoryKey === -1) {
          dispatch(putChecklistConfig(newConfig));
          dispatch(updateChecklistConfig(newConfig));
        } else {
          if (otherCatKey > -1) {
            dispatch(putChecklistCategoryConfig(otherCategoryConfig));
            dispatch(updateChecklistCategoryConfig(otherCategoryConfig));
          }
          dispatch(putChecklistCategoryConfig(newCategoryConfig));
          dispatch(updateChecklistCategoryConfig(newCategoryConfig));
        }

        // Saves
        if (categoryKey === -1) {
          // Categories - Saved in new order (items not affected)
          await put(`/api/checklists/${checklist.checkListCode}/categories`, {}, newCategories);
        } else {
          // Questions across categories save and update in 2 parts - first remove item from a previous category if needed then save new category
          if (otherCatKey > -1) {
            await put(`/api/checklists/${checklist.checkListCode}/categories/${otherCatKey}/items`, {}, otherCategoryItems);
          }
          await put(`/api/checklists/${checklist.checkListCode}/categories/${categoryKey}/items`, {}, newItems);
        }

        // Message
        dispatch(addMessage('success', 'questionUpdated', { item: newConfig.description }));
        dispatch(requestSucceded(requestId));
      } catch (error) {
        // ERROR
        dispatch(requestFailed(requestId, error));

        // Put back original states as failure
        dispatch(putChecklistConfig(checklist));
        dispatch(updateChecklistConfig(checklist));
      }
    };
  }
  return () => {};
}
